/**
 * Seletor de entidade (DS-40 · spec 05 §1).
 *
 * É o **contexto de visualização**, e existe UM em todo o sistema — no cabeçalho.
 * Nenhuma tela duplica seletor de entidade no conteúdo (GLOSSARIO). Não confundir
 * com o campo "Entidade emissora" dos formulários, que é o DADO do registro.
 *
 * A lei que este componente carrega: entidade sem dado mostra **estado vazio
 * explícito**, nunca os números de outra entidade sob o rótulo trocado. Quem cumpre
 * isso são as páginas — o seletor só anuncia a troca.
 */
import { useEffect, useRef, useState } from 'react'
import { Icone } from './icone.tsx'
import { useTextos } from '../textos.ts'
import { TEXTOS_DO_SHELL } from './shell-textos.ts'

export interface Entidade {
  readonly id: string
  readonly nome: string
  readonly meta: string
  readonly ambiente: 'producao' | 'sandbox' | 'consolidado'
}

interface Props {
  readonly entidades: readonly Entidade[]
  readonly ativa: Entidade | null
  readonly aoTrocar: (entidade: Entidade) => void
}

export function SeletorDeEntidade({ entidades, ativa, aoTrocar }: Props) {
  const texto = useTextos(TEXTOS_DO_SHELL)
  const [aberto, setAberto] = useState(false)
  const caixa = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!aberto) return
    const fechar = (evento: MouseEvent): void => {
      if (caixa.current !== null && !caixa.current.contains(evento.target as Node)) setAberto(false)
    }
    const porTecla = (evento: KeyboardEvent): void => {
      if (evento.key === 'Escape') setAberto(false)
    }
    document.addEventListener('mousedown', fechar)
    document.addEventListener('keydown', porTecla)
    return () => {
      document.removeEventListener('mousedown', fechar)
      document.removeEventListener('keydown', porTecla)
    }
  }, [aberto])

  return (
    // O `hidden sm:block` fica no INVÓLUCRO, não só no botão: no protótipo o seletor
    // some por inteiro abaixo de sm, e um invólucro visível — mesmo vazio — continua
    // sendo item de flex e consome um `gap-3` da topbar. Foi o que a baseline
    // mobile acusou: a caixa de busca nascia com largura diferente da do protótipo.
    <div className="relative hidden sm:block" ref={caixa}>
      <button
        type="button"
        className={`flex items-center gap-2 h-9 px-3 rounded-[10px] border text-[13px] hover:border-navy/40 ${
          ativa?.ambiente === 'sandbox'
            ? 'bg-amber-50 border-amber-300 text-amber-900'
            : 'bg-white border-hairline text-text2'
        }`}
        onClick={() => {
          setAberto((v) => !v)
        }}
        aria-expanded={aberto}
        aria-haspopup="listbox"
      >
        <Icone nome={ativa?.ambiente === 'sandbox' ? 'flask-conical' : 'building-2'} className="w-4 h-4 text-muted" />
        <span className="font-medium">{ativa?.nome ?? texto('entidade')}</span>
        <Icone nome="chevron-down" className="w-4 h-4 text-faint" />
      </button>

      {aberto && (
        <ul
          role="listbox"
          className="absolute right-0 z-30 mt-2 w-64 rounded-[10px] bg-white border border-hairline shadow-card py-1 text-sm"
        >
          {entidades.map((entidade) => (
            <li key={entidade.id}>
              <button
                type="button"
                role="option"
                aria-selected={entidade.id === ativa?.id}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-navy/5"
                onClick={() => {
                  aoTrocar(entidade)
                  setAberto(false)
                }}
              >
                <span className="min-w-0">
                  <span className="block truncate text-text2">{entidade.nome}</span>
                  <span className={`block text-[11px] ${entidade.ambiente === 'sandbox' ? 'text-amber-700 font-medium' : 'text-faint'}`}>
                    {entidade.ambiente === 'sandbox' ? `${texto('ambienteDeTestes')} · ${entidade.meta}`
                      : entidade.ambiente === 'consolidado' ? `${texto('consolidado')} · ${entidade.meta}` : entidade.meta}
                  </span>
                </span>
                {entidade.id === ativa?.id && <Icone nome="check" className="w-4 h-4 text-navy shrink-0" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
